import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { setAuthState } from "../composables/auth.js";
import { PLAN_STEPS, COMPONENTS_MAP } from "../utils/job-search-plans";
import { isJobSearchBlockersEnabled } from "../utils/helpers";
import { useResumesStore } from "./resumes.store";
import { user } from "../api/auth";
import { resetStageNotification } from "../api/job-search-plan";

const { getUser, isAuthenticated } = setAuthState();

export const useJobSearchPlanStore = defineStore("job-search-plan", () => {
    const userData = getUser();

    const resumeStore = useResumesStore();
    const showModal = ref(false);

    const homePassed = userData?.options?.home_passed ?? false;
    const jobPlanData = ref(userData?.job_search_blockers);
    const isCycleRestarted = ref(jobPlanData.value?.is_reset_to_stage_1 ?? false);
    const isMovedToNextStage = ref(jobPlanData.value?.moved_to_next_stage ?? false);

    const userStep = ref(jobPlanData.value?.current_stage);
    const primaryResumeScore = ref(jobPlanData.value?.highest_resume_score || 0);
    const jobsApplied = ref(jobPlanData.value?.total_job_applications || 0);

    const interviewsCount = ref(jobPlanData.value?.total_interview_count || 0);
    const interviewRate = ref(jobPlanData.value?.interview_rate || 0);
    const timeToInterview = ref(jobPlanData.value?.time_to_interview || 0);

    const offersCount = ref(jobPlanData.value?.total_offer_count || 0);
    const offerRate = ref(jobPlanData.value?.offer_rate || 0);
    const timeToOffer = ref(jobPlanData.value?.time_to_offer || 0);

    const userSteps = {
        target_job: PLAN_STEPS.targetJobStage,
        resume: PLAN_STEPS.resumeStage,
        applying: PLAN_STEPS.applyStage,
        interview: PLAN_STEPS.interviewStage,
        offer: PLAN_STEPS.offerStage,
    };

    const isSidebarVisible = ref(!homePassed && isJobSearchBlockersEnabled());
    const steps = ref(Object.values(PLAN_STEPS));
    const currentStep = ref(userSteps[userStep.value]);

    const currentStepData = computed(() => COMPONENTS_MAP[currentStep.value]);
    const currentStepIndex = computed(() => steps.value.indexOf(currentStep.value));

    const refresh = async () => {
        if(!isAuthenticated()) return;

        resumeStore.fetchResumes();
        showModal.value = false;
        
        const result = await user();
        if(!result) return;
        
        const updatedJobPlanData = result?.data?.data?.job_search_blockers;

        isCycleRestarted.value = updatedJobPlanData?.is_reset_to_stage_1 ?? false;
        isMovedToNextStage.value = updatedJobPlanData?.moved_to_next_stage ?? false;
        userStep.value = updatedJobPlanData?.current_stage;
        primaryResumeScore.value = updatedJobPlanData?.highest_resume_score || 0;
        jobsApplied.value = updatedJobPlanData?.total_job_applications || 0;

        interviewsCount.value = updatedJobPlanData?.total_interview_count || 0;
        interviewRate.value = updatedJobPlanData?.interview_rate || 0;
        timeToInterview.value = updatedJobPlanData?.time_to_interview || 0;

        offersCount.value = updatedJobPlanData?.total_offer_count || 0;
        offerRate.value = updatedJobPlanData?.offer_rate || 0;
        timeToOffer.value = updatedJobPlanData?.time_to_offer || 0;

        currentStep.value = userSteps[userStep.value];
    };

    const resetStage = async () => {
        await resetStageNotification();

        setTimeout(() => {
            isMovedToNextStage.value = false;
        }, 2000);
    }

    return {
        primaryResumeScore,
        jobsApplied,
        userSteps,

        interviewsCount,
        interviewRate,
        timeToInterview,

        offersCount,
        offerRate,
        timeToOffer,

        isSidebarVisible,
        isCycleRestarted,
        isMovedToNextStage,
        steps,
        currentStep,
        currentStepData,
        currentStepIndex,
        showModal,
        refresh,
        resetStage
    };
});
